import React, { useState, useEffect } from 'react';
import _, { set } from 'lodash';
import { type Section as SectionType, type PortalSettings } from '@gorilla/portal-shared/src/schemas';
import { Heading, Text, Icon, Divider, Box, Flex, Button, IconButton, Tooltip } from '@vibe/core';
import { File, AddSmall, DropdownChevronLeft, DropdownChevronRight } from '@vibe/icons';
import { type Board as BoardType } from '@gorilla/common/src/lib/monday-api/api';
import { Page } from './Page';
import { PageList } from './PageList';
import { isSectionOpenByDefault } from './SectionMeta';
import { createRandomId } from '../lib/utils';
import { ConfirmModal } from './ConfirmModal';
import { type Portal } from '../services/portal';

type OpenSectionIds = Record<string, boolean>;

type PagesProps = {
  clientBoard: BoardType;
  clientBoardItems: any;
  portal: Portal;
  portalSettings: PortalSettings;
  setCustomizationModal: any;
  customizationModal: any;
  boardSourceIdsInUse: string[];
  onChange: (newPortalSettings: PortalSettings) => void;
  onManageBoardSourcesClick: () => void;
  onEditBoardSourceClick: (boardSourceId: string) => void;
  onShowPlaceholdersModalClick: () => void;
  clientBoardSubitemsBoardId: number | null;
  screenMode: 'small' | 'medium' | 'large';
  hasUnpublishedChanges: boolean | undefined;
  onPreviewPortal?: () => void;
};

export function Pages({
  portal,
  portalSettings,
  onChange,
  setCustomizationModal,
  customizationModal,
  onManageBoardSourcesClick,
  onEditBoardSourceClick,
  onShowPlaceholdersModalClick,
  clientBoardSubitemsBoardId,
  clientBoard,
  clientBoardItems,
  screenMode,
  hasUnpublishedChanges,
  onPreviewPortal,
}: PagesProps) {
  const portalId = portal?.id;
  const [showOnboardingDialogTips, setShowOnboardingDialogTips] = useState(() => {
    return window.localStorage.getItem(`cpb-${portalId}-onboarding-dialog--dismissed`) !== 'yes';
  });

  const handleDismissOnboarding = () => {
    window.localStorage.setItem(`cpb-${portalId}-onboarding-dialog--dismissed`, 'yes');
  };

  const [togglePortalPages, setTogglePortalPages] = useState<boolean>(true || screenMode === 'small' ? false : true);
  const [pageIdToDelete, setPageIdToDelete] = useState<string | null>(null);
  const [openSectionIds, setOpenSectionIds] = useState<OpenSectionIds>({});
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);

  let selectedPage = portalSettings.pages.find((page) => page.id === selectedPageId);

  if (!selectedPage && portalSettings.pages.length > 0) {
    selectedPage = portalSettings.pages[0];
  }

  const normalizedSelectedPageId = selectedPage ? selectedPage.id : null;

  function addPage() {
    const newPageId = createRandomId();

    onChange({
      ...portalSettings,
      pages: [
        ...portalSettings.pages,
        {
          id: newPageId, // TODO: backend should check for unique ids
          name: 'New page',
          sections: [],
        },
      ],
    });

    setSelectedPageId(newPageId);
  }

  function toggleSectionOpen(section: SectionType, targetIsOpen?: boolean) {
    if (_.isBoolean(targetIsOpen)) {
      setOpenSectionIds({ ...openSectionIds, [section.id]: targetIsOpen });
    } else {
      setOpenSectionIds({ ...openSectionIds, [section.id]: !openSectionIds[section.id] });
    }
  }

  // Ensure all sections have an explicit open state
  useEffect(() => {
    const sections = selectedPage?.sections || [];
    const missingSections = sections.filter((s) => !_.isBoolean(openSectionIds[s.id]));

    if (missingSections.length > 0) {
      const newState = { ...openSectionIds };
      missingSections.forEach((s) => {
        newState[s.id] = isSectionOpenByDefault(s);
      });
      setOpenSectionIds(newState);
    }
  }, [selectedPage?.sections, openSectionIds]);

  const currentOpenSectionIds = (selectedPage?.sections || []).filter((s) => openSectionIds[s.id]).map((s) => s.id);

  return (
    <div
      style={{
        paddingLeft: screenMode === 'small' ? (togglePortalPages ? '64px' : '64px') : '320px',
      }}
    >
      {pageIdToDelete !== null ? (
        <ConfirmModal
          isOpen={true}
          isConfirming={false}
          headline="Delete page"
          text="Are you sure you want to delete this page?"
          info="By deleting, this page including all content sections are irretrievably removed from your portal configuration."
          confirmButtonLabel="Delete page"
          onConfirm={async () => {
            onChange({
              ...portalSettings,
              pages: portalSettings.pages.filter((p) => p.id !== pageIdToDelete),
            });
            setSelectedPageId(null);
            setPageIdToDelete(null);
          }}
          onClose={() => {
            setPageIdToDelete(null);
          }}
        />
      ) : null}

      <Flex
        justify="start"
        align="start"
        style={{
          position: 'relative',
          pointerEvents: showOnboardingDialogTips ? 'none' : 'auto',
        }}
      >
        {/* SIDEBAR */}

        {selectedPage ? (
          <div
            className={
              screenMode === 'small' && !togglePortalPages
                ? 'portalmanager__content__sidebar portalmanager__content__sidebar--narrow'
                : 'portalmanager__content__sidebar'
            }
            style={{
              borderRight: screenMode === 'small' && togglePortalPages ? 'none' : undefined,
              boxShadow: screenMode === 'small' && togglePortalPages ? '4px 0px 6px rgba(0, 0, 0, 0.1)' : undefined,
            }}
          >
            {/* PAGES --START-- */}

            <div
              style={{
                zIndex: 500,
                position: 'sticky',
                top: 0,
                padding: '0px 16px 0 16px',
                width: 'calc(100% - 32px)',
                height: '72px',
                borderBottom: '1px solid #D0D4E4',
                backgroundColor: '#fff',
                boxShadow: '0px 4px 6px -4px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Flex style={{ height: 'inherit' }}>
                <Flex gap={0}>
                  {screenMode === 'small' ? (
                    <IconButton
                      icon={togglePortalPages ? DropdownChevronLeft : DropdownChevronRight}
                      kind="tertiary"
                      size="small"
                      onClick={() => {
                        setTogglePortalPages(!togglePortalPages);
                      }}
                      tooltipContent={togglePortalPages ? '' : 'Open portal pages'}
                      tooltipProps={{
                        position: 'right',
                        showOnDialogEnter: false,
                      }}
                    />
                  ) : null}
                  {togglePortalPages || screenMode !== 'small' ? (
                    <Heading
                      type="h3"
                      weight="bold"
                      align="start"
                      ellipsis
                      style={{
                        marginLeft: '10px',
                      }}
                    >
                      Portal pages
                    </Heading>
                  ) : null}
                </Flex>
                {togglePortalPages || screenMode !== 'small' ? (
                  <Box style={{ marginLeft: 'auto' }}>
                    <Tooltip content="Create page" position="top" showOnDialogEnter={false}>
                      <Box>
                        <IconButton
                          icon={AddSmall}
                          kind="primary"
                          size="small"
                          onClick={() => {
                            addPage();
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </Box>
                ) : null}
              </Flex>
            </div>
            <div
              style={{
                overflowY: 'auto',
                width: 'inherit',
                height: '100%',
                maxHeight: 'calc(100vh - (73px + 72px))',
              }}
            >
              <PageList
                pages={portalSettings.pages}
                selectedPageId={normalizedSelectedPageId}
                togglePortalPages={togglePortalPages}
                screenMode={screenMode}
                onPageSelect={(pageId) => {
                  setSelectedPageId(pageId);
                  setTogglePortalPages(false);
                }}
                onPageReorder={(newPages) => {
                  onChange({ ...portalSettings, pages: newPages });
                }}
                onDeleteClick={(pageId) => {
                  setPageIdToDelete(pageId);
                }}
              />
            </div>
          </div>
        ) : null}

        {/* CONTENT */}

        <div
          className="portalmanager__content--inlinesidebar"
          style={{
            pointerEvents: togglePortalPages && screenMode === 'small' ? 'none' : 'auto',
            overflow: !selectedPage ? 'visible' : 'hidden',
            flex: 1,
            transform: !selectedPage ? (screenMode === 'small' ? 'translate3d(-64px,0,0)' : 'translate3d(-320px,0,0)') : 'none',
          }}
        >
          {!selectedPage ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '24px',
                width: 'calc(100vw - 48px)',
                height: 'calc(100vh - 120px)',
                minHeight: '300px',
                border: '1px dashed #D0D4E4',
                borderRadius: '4px',
              }}
            >
              <Flex
                direction="column"
                gap={20}
                style={{
                  marginTop: '-32px',
                }}
              >
                <Flex direction="column" gap={4}>
                  <Flex direction="column" gap={8}>
                    <Icon icon={File} size={28} />
                    <Heading type="h3" weight="bold" align="center">
                      Add your first page
                    </Heading>
                  </Flex>
                  <Text type="text2" align="center" color="secondary">
                    Create a portal page to start <br />
                    building your client portal.
                  </Text>
                </Flex>
                <IconButton
                  icon={AddSmall}
                  kind="primary"
                  size="small"
                  color="primary"
                  onClick={() => {
                    addPage();
                  }}
                />
              </Flex>
            </div>
          ) : (
            <div
              style={{
                pointerEvents: showOnboardingDialogTips ? 'none' : 'auto',
              }}
            >
              <Page
                key={selectedPage.id}
                clientBoard={clientBoard}
                clientBoardItems={clientBoardItems}
                portalSettings={portalSettings}
                onChangePortalSettings={onChange}
                page={selectedPage}
                clientBoardSubitemsBoardId={clientBoardSubitemsBoardId}
                onManageBoardSourcesClick={onManageBoardSourcesClick}
                onEditBoardSourceClick={onEditBoardSourceClick}
                onShowPlaceholdersModalClick={onShowPlaceholdersModalClick}
                onDelete={() => {
                  onChange({
                    ...portalSettings,
                    pages: portalSettings.pages.filter((p) => p.id !== normalizedSelectedPageId),
                  });
                  setSelectedPageId(null);
                }}
                onChange={(newPage) => {
                  onChange({
                    ...portalSettings,
                    pages: portalSettings.pages.map((p) => (p.id === normalizedSelectedPageId ? newPage : p)),
                  });
                  // A page's id is its alias, so renaming the alias changes the id. Keep the selection
                  // pointing at the renamed page; otherwise the lookup falls back to the first page.
                  if (newPage.id !== normalizedSelectedPageId) {
                    setSelectedPageId(newPage.id);
                  }
                }}
                customizationModal={customizationModal}
                setCustomizationModal={setCustomizationModal}
                screenMode={screenMode}
                hasUnpublishedChanges={hasUnpublishedChanges}
                openSectionIds={currentOpenSectionIds}
                toggleSectionOpen={toggleSectionOpen}
                showOnboardingDialogTips={showOnboardingDialogTips}
                setShowOnboardingDialogTips={setShowOnboardingDialogTips}
                onDismiss={handleDismissOnboarding}
                onPreviewPortal={onPreviewPortal}
              />
            </div>
          )}
          {togglePortalPages && screenMode === 'small' ? (
            <div
              style={{
                pointerEvents: 'none',
                zIndex: 799,
                position: 'fixed',
                inset: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(41, 47, 76, 0.7)',
              }}
            >
              &nbsp;
            </div>
          ) : null}
        </div>
      </Flex>

      {/* Onboarding Overlay */}
      {showOnboardingDialogTips ? (
        <div
          style={{
            pointerEvents: 'none',
            zIndex: 4999,
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(41, 47, 76, 0.2)',
          }}
        >
          &nbsp;
        </div>
      ) : null}
    </div>
  );
}
